import { devLog } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import {
  Users, DollarSign, Calendar, CheckCircle, XCircle, Loader2,
  Shield, Clock, TrendingUp, Mail, Heart, Briefcase, Sparkles,
  ArrowLeft, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeLabels = { therapist: 'Terapeuta', coach: 'Mentor', spiritualist: 'Espiritualista' };
const typeColors = { therapist: 'bg-[#6B8E7C]', coach: 'bg-[#D4A373]', spiritualist: 'bg-[#9B8BAE]' };
const typeIcons = { therapist: Heart, coach: Briefcase, spiritualist: Sparkles };

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card-theraflow p-5">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
        <span className="text-sm text-[#63635E]">{label}</span>
      </div>
      <p className="text-2xl font-light text-[#292927]">{value}</p>
    </div>
  );
}

const emailStatusBg = (status) => {
  if (status === 'sent') return 'bg-[#6B8E7C]/10';
  if (status === 'failed') return 'bg-[#B85C5C]/10';
  return 'bg-[#D4A373]/10';
};

const emailStatusText = (status) => {
  if (status === 'sent') return 'text-[#6B8E7C]';
  if (status === 'failed') return 'text-[#B85C5C]';
  return 'text-[#D4A373]';
};

const emailBadgeColor = (status) => {
  if (status === 'sent') return 'bg-[#6B8E7C]/10 text-[#6B8E7C]';
  if (status === 'failed') return 'bg-[#B85C5C]/10 text-[#B85C5C]';
  return 'bg-[#D4A373]/10 text-[#D4A373]';
};

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [profFilter, setProfFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'professionals') fetchProfessionals();
    if (tab === 'emails') fetchEmails();
  }, [tab, profFilter]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/admin/stats`, { headers, withCredentials: true });
      setStats(data);
    } catch (err) {
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfessionals = async () => {
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/admin/professionals?status=${profFilter}`, { headers, withCredentials: true });
      setProfessionals(data);
    } catch (err) {
      devLog.error(err);
    }
  };

  const fetchEmails = async () => {
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/admin/emails`, { headers, withCredentials: true });
      setEmails(data);
    } catch (err) {
      devLog.error(err);
    }
  };

  const handleVerify = async (profId, action) => {
    setActionLoading(profId);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.put(`${API_URL}/api/admin/professionals/${profId}/verify`, { action }, { headers, withCredentials: true });
      toast.success(data.message);
      fetchProfessionals();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally {
      setActionLoading(null);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-16 h-16 mx-auto text-[#B85C5C] mb-4" strokeWidth={1} />
            <p className="text-[#B85C5C] text-lg">Acesso restrito a administradores</p>
            <Link to="/dashboard" className="btn-secondary mt-4 inline-block">Voltar ao Dashboard</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="admin-page">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-light text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Painel Administrativo
              </h1>
              <p className="text-[#63635E]">Gerencie a plataforma Betherapy</p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5D4E]/10">
              <Shield className="w-4 h-4 text-[#4A5D4E]" />
              <span className="text-sm font-medium text-[#4A5D4E]">Admin</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 p-1 bg-[#EAE8E3] rounded-xl w-fit" data-testid="admin-tabs">
            {[
              { id: 'overview', label: 'Visão Geral', icon: TrendingUp },
              { id: 'professionals', label: 'Profissionais', icon: Users },
              { id: 'emails', label: 'Emails', icon: Mail }
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  tab === t.id ? 'bg-white text-[#292927] shadow-sm' : 'text-[#63635E] hover:text-[#292927]'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" strokeWidth={1.5} />
                {t.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {tab === 'overview' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" /></div>
              ) : stats ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
                    <StatCard icon={Users} label="Total Usuários" value={stats.total_users} color="text-[#4A5D4E]" />
                    <StatCard icon={Users} label="Profissionais" value={stats.total_professionals} color="text-[#6B8E7C]" />
                    <StatCard icon={Calendar} label="Sessões" value={stats.total_sessions} color="text-[#D4A373]" />
                    <StatCard icon={DollarSign} label="Receita Plataforma" value={`R$ ${(stats.platform_revenue || 0).toFixed(2)}`} color="text-[#4A5D4E]" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard icon={Users} label="Clientes" value={stats.total_clients} color="text-[#63635E]" />
                    <StatCard icon={CheckCircle} label="Sessões Concluídas" value={stats.completed_sessions} color="text-[#6B8E7C]" />
                    <StatCard icon={DollarSign} label="Volume Total" value={`R$ ${(stats.gross_revenue || 0).toFixed(2)}`} color="text-[#D4A373]" />
                    <StatCard icon={Clock} label="Pendentes Verificação" value={stats.pending_verification} color="text-[#B85C5C]" />
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Professionals Tab */}
          {tab === 'professionals' && (
            <div>
              <div className="flex gap-2 mb-6">
                {[
                  { id: 'pending', label: 'Pendentes' },
                  { id: 'verified', label: 'Verificados' },
                  { id: 'all', label: 'Todos' }
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setProfFilter(f.id)}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      profFilter === f.id ? 'bg-[#4A5D4E] text-white' : 'bg-[#EAE8E3] text-[#63635E]'
                    }`}
                    data-testid={`filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {professionals.length === 0 ? (
                <div className="card-theraflow p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-[#A0A09A] mb-3" strokeWidth={1} />
                  <p className="text-[#63635E]">Nenhum profissional {profFilter === 'pending' ? 'pendente' : ''}</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="professionals-list">
                  {professionals.map(prof => {
                    const TypeIcon = typeIcons[prof.professional_type] || Heart;
                    return (
                      <div key={prof.id} className="card-theraflow p-5 flex items-center gap-4" data-testid={`prof-${prof.id}`}>
                        <div className={`w-12 h-12 rounded-xl ${typeColors[prof.professional_type] || 'bg-[#4A5D4E]'} flex items-center justify-center flex-shrink-0`}>
                          <TypeIcon className="w-6 h-6 text-white" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#292927]">{prof.name}</p>
                            {prof.verified && <CheckCircle className="w-4 h-4 text-[#6B8E7C]" strokeWidth={2} />}
                            {prof.featured_plan && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4A373]/10 text-[#D4A373]">{prof.featured_plan}</span>
                            )}
                          </div>
                          <p className="text-sm text-[#63635E]">{typeLabels[prof.professional_type]} - {prof.email}</p>
                          <p className="text-xs text-[#A0A09A] truncate">{prof.specializations?.join(', ')}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {prof.rating_avg > 0 && (
                            <span className="text-sm text-[#D4A373]">★ {prof.rating_avg.toFixed(1)}</span>
                          )}
                          {!prof.verified ? (
                            <>
                              <button
                                onClick={() => handleVerify(prof.id, 'approve')}
                                disabled={actionLoading === prof.id}
                                className="px-3 py-1.5 rounded-lg bg-[#6B8E7C] text-white text-sm hover:bg-[#5A7D6B] transition-colors flex items-center gap-1"
                                data-testid={`approve-${prof.id}`}
                              >
                                {actionLoading === prof.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                                Aprovar
                              </button>
                              <button
                                onClick={() => handleVerify(prof.id, 'reject')}
                                disabled={actionLoading === prof.id}
                                className="px-3 py-1.5 rounded-lg bg-[#B85C5C] text-white text-sm hover:bg-[#A04C4C] transition-colors flex items-center gap-1"
                                data-testid={`reject-${prof.id}`}
                              >
                                <XCircle className="w-3 h-3" />
                                Rejeitar
                              </button>
                            </>
                          ) : (
                            <span className="text-xs px-2.5 py-1 rounded-full bg-[#6B8E7C]/10 text-[#6B8E7C]">Verificado</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Emails Tab */}
          {tab === 'emails' && (
            <div>
              {emails.length === 0 ? (
                <div className="card-theraflow p-8 text-center">
                  <Mail className="w-12 h-12 mx-auto text-[#A0A09A] mb-3" strokeWidth={1} />
                  <p className="text-[#63635E]">Nenhum email enviado ainda</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="email-log">
                  {emails.map((email, idx) => (
                    <div key={email.email_id || idx} className="card-theraflow p-4 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${emailStatusBg(email.status)}`}>
                        <Mail className={`w-4 h-4 ${emailStatusText(email.status)}`} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#292927] truncate">{email.subject}</p>
                        <p className="text-xs text-[#A0A09A]">{email.to} - {new Date(email.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${emailBadgeColor(email.status)}`}>{email.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
